import { Box, Button, Link, Text } from '@factorialco/deprecated-design-system'
import { F0Icon, F0TagStatus } from '@factorialco/f0-react'
import { ArrowCycle, ArrowRight, LockLocked } from '@factorialco/f0-react/icons/app'
import {
  CompensationsPayrollRunDocument,
  CycleRouteMetaDocument,
  PayrollRunInfoPayrollRunFragment,
  TogglePayrollRunLockDocument
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  LegacyPayrollRunStatus,
  PayrollRunStatus,
  getPayrollRunStatusText,
  getPayrollRunStatusVariant
} from 'modules/compensations/lib/payrollRunStatus'

import helpCenterUrl from 'modules/core/lib/helpCenterUrl'

type PayrollRunInfoProps = PayrollRunInfoPayrollRunFragment

interface LockPayrollRunStatusTransitionModalProps {
  payrollRun: PayrollRunInfoProps
  onSuccess: () => void
  lock: boolean
  onClose: () => void
}

const LockPayrollRunStatusTransitionModal = ({
  payrollRun,
  onSuccess,
  onClose,
  lock
}: LockPayrollRunStatusTransitionModalProps) => {
  // FIXME: Once i18nDateToString is ressurected move the format to the i18n translations
  const formatedStartsOn = stringToDate(payrollRun.startsOn, 'backend_datetime').format('MMMM YYYY')

  const [togglePayrollRunLock] = useMutation(TogglePayrollRunLockDocument, {
    extractorFn: (result) => result.compensationsMutations.toggleLockCompensationsPayrollRun
  })

  const title = i18n.t('compensations.PayrollRunCompensationsList.lock_modal_cycle_lock_title', {
    period: formatedStartsOn
  })

  const currentStatus = payrollRun.status || PayrollRunStatus.OPEN || LegacyPayrollRunStatus.OPENED
  const nextStatus = PayrollRunStatus.IN_PREPARATION

  const currentStatusText = getPayrollRunStatusText(currentStatus)
  const nextStatusText = getPayrollRunStatusText(nextStatus)
  const currentStatusVariant = getPayrollRunStatusVariant(currentStatus)
  const nextStatusVariant = getPayrollRunStatusVariant(nextStatus)

  const handleConfirm = async () => {
    const result = await togglePayrollRunLock({
      variables: {
        lock,
        payrollRunId: payrollRun.id
      },
      refetchQueries: [CompensationsPayrollRunDocument, CycleRouteMetaDocument]
    })

    if (lock) {
      track(trackingEvents.compensations.lockPayrollRun, {
        payrollRunId: payrollRun.id
      })
    } else {
      track(trackingEvents.compensations.unlockPayrollRun, {
        payrollRunId: payrollRun.id
      })
    }

    const handled = handleResult(result, () => undefined)

    if (handled.success && handled.data.payrollRun) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully')
      })

      onSuccess()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error')
      })
    }

    return handled
  }

  return (
    <Modal
      illustration="permissions"
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button type="primary" onClick={handleConfirm}>
            {i18n.t('common.confirm')}
          </Button>
        </>
      }
    >
      <Box flexDirection="column" gap="s24">
        <Box alignItems="center" flexDirection="row" gap="s12">
          <F0TagStatus text={currentStatusText} variant={currentStatusVariant} />
          <F0Icon icon={ArrowRight} />
          <F0TagStatus text={nextStatusText} variant={nextStatusVariant} />
        </Box>

        <Callout
          type="warning"
          compact
          description={i18n.t(
            'compensations.PayrollRunCompensationsList.lock_modal_action_can_not_be_undone_callout'
          )}
        />

        <Box flexDirection="column" gap="s12">
          <Text.Normal size="300" color="textSecondary">
            {i18n.t('compensations.PayrollRunCompensationsList.lock_modal_consequences_title')}
          </Text.Normal>

          <Box flexDirection="column" gap="s8">
            <Box alignItems="flexStart" flexDirection="row" gap="s8">
              <Box paddingTop="s2">
                <F0Icon icon={LockLocked} />
              </Box>
              <Text.Normal size="300" color="textPrimary">
                {i18n.t('compensations.PayrollRunCompensationsList.lock_modal_edits_prevented')}
              </Text.Normal>
            </Box>

            <Box alignItems="flexStart" flexDirection="row" gap="s8">
              <Box paddingTop="s2">
                <F0Icon icon={ArrowCycle} />
              </Box>
              <Text.Normal size="300" color="textPrimary">
                {i18n.t('compensations.PayrollRunCompensationsList.lock_modal_syncing_disabled')}
              </Text.Normal>
            </Box>
          </Box>
        </Box>

        <>
          <Link.Href
            href={helpCenterUrl('compensations_cycle_statuses')}
            target="_blank"
            rel="nofollow noopener noreferrer"
          >
            {i18n.t('compensations.PayrollRunCompensationsList.learn_more_cycle_statuses')}
          </Link.Href>
        </>
      </Box>
    </Modal>
  )
}

export { LockPayrollRunStatusTransitionModal }
